// Geld: immer ganze Cent (Integer). Keine Gleitkomma-Summen, Rundung genau einmal und kaufmännisch.
import { assertSafeInt, mulDiv, parseDecimalDe, type RoundingMode } from './decimal'
import type { Qty } from './qty'

/** Betrag in Cent, ganzzahlig, darf negativ sein. */
export type Cents = number

/** Steuersatz in Basispunkten: 19 % = 1900, 7 % = 700, 10,7 % = 1070. */
export type RateBp = number

export function assertCents(value: number, label = 'Betrag'): Cents {
  return assertSafeInt(value, label)
}

export function sumCents(values: Iterable<Cents>): Cents {
  let total = 0
  for (const v of values) total += assertCents(v)
  return assertCents(total, 'Summe')
}

/**
 * Zeilenbetrag = Menge × Preis ÷ Preiseinheit, kaufmännisch gerundet.
 * `qty` und `pricePerQty` in Tausendstel der Basiseinheit. Preise unter einem Cent entstehen über die
 * Preiseinheit: 0,8125 €/Flasche = 8125 Cent je 100 Flaschen → lineAmount(qty, 8125, 100_000).
 */
export function lineAmount(qty: Qty, priceCents: Cents, pricePerQty: Qty = 1000): Cents {
  if (pricePerQty <= 0) throw new RangeError('Die Preiseinheit muss größer als null sein.')
  return mulDiv(qty, priceCents, pricePerQty, 'half-up')
}

/** Anteil in Basispunkten: percentOf(10000, 250) = 250 (2,5 % von 100,00 €). */
export function percentOf(amount: Cents, basisPoints: number, mode: RoundingMode = 'half-up'): Cents {
  return mulDiv(amount, basisPoints, 10_000, mode)
}

export interface TaxSplit {
  net: Cents
  tax: Cents
  gross: Cents
}

/** Brutto → Netto + Steuer. Die Steuer wird gerundet, Netto ist der Rest (net + tax = gross gilt immer). */
export function splitGross(gross: Cents, rateBp: RateBp): TaxSplit {
  assertRate(rateBp)
  const tax = mulDiv(gross, rateBp, 10_000 + rateBp, 'half-up')
  return { net: gross - tax, tax, gross }
}

/** Netto → Steuer + Brutto. */
export function addTax(net: Cents, rateBp: RateBp): TaxSplit {
  assertRate(rateBp)
  const tax = mulDiv(net, rateBp, 10_000, 'half-up')
  return { net, tax, gross: net + tax }
}

function assertRate(rateBp: number): void {
  if (!Number.isInteger(rateBp) || rateBp < 0 || rateBp > 10_000) {
    throw new RangeError(`Steuersatz in Basispunkten erwartet (0–10000), erhalten: ${rateBp}`)
  }
}

/**
 * Verteilt `total` proportional zu `weights` nach dem Verfahren der größten Reste (Hare/Niemeyer).
 * Die Teile sind ganze Cent und ergeben zusammen exakt `total` – auch bei negativem Betrag.
 * Gleichstand entscheidet die Reihenfolge (früheres Element zuerst). Gewichte dürfen nicht negativ sein.
 */
export function allocate(total: Cents, weights: readonly number[]): Cents[] {
  assertCents(total, 'Gesamtbetrag')
  if (weights.length === 0) {
    if (total !== 0) throw new RangeError('Ohne Empfänger lässt sich kein Betrag verteilen.')
    return []
  }
  for (const w of weights) {
    if (!Number.isSafeInteger(w) || w < 0) throw new RangeError(`Gewichte müssen nicht negative Ganzzahlen sein: ${w}`)
  }
  const weightSum = weights.reduce((acc, w) => acc + BigInt(w), 0n)
  // Sind alle Gewichte null, wird gleichmäßig verteilt.
  const effective = weightSum === 0n ? weights.map(() => 1n) : weights.map((w) => BigInt(w))
  const denominator = weightSum === 0n ? BigInt(weights.length) : weightSum
  const negative = total < 0
  const amount = BigInt(Math.abs(total))

  const shares = effective.map((w) => (amount * w) / denominator)
  const remainders = effective.map((w, index) => ({ index, remainder: (amount * w) % denominator }))
  let rest = amount - shares.reduce((acc, s) => acc + s, 0n)
  remainders.sort((a, b) => (a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1))
  for (const { index } of remainders) {
    if (rest === 0n) break
    shares[index] = (shares[index] as bigint) + 1n
    rest -= 1n
  }
  // `|| 0` verhindert −0 bei negativen Beträgen mit Null-Anteilen
  return shares.map((s) => (negative ? -Number(s) : Number(s)) || 0)
}

/** Deutsche Geldeingabe → Cent: '1.234,50 €' → 123450, '12*0,99' → 1188. Ungültig → null. */
export function parseEuro(text: string): Cents | null {
  return parseDecimalDe(text, 2)
}

/** Cent → Euro als Zahl. Nur für Anzeige/Diagramme, nie weiterrechnen. */
export const centsToEuro = (cents: Cents): number => cents / 100
